<script lang="ts" setup>
  import type { ClpInfluencerBlockStoryblok } from '@/storyblok/component-types-sb'
  import type { IClpInfluencerDestinations, IImageSlide } from '@/components/clp/influencer-block/types'
  import type { IDestinationItem, IOriginItem } from '@/components/clp/types'

  defineProps<{
    blok: ClpInfluencerBlockStoryblok
  }>()

  type TDestinations = Omit<IClpInfluencerDestinations, 'slides'>

  const origDestRows = useState<IDestinationItem[]>('orig-dest-rows', () => [])

  const mappedData = computed<Map<string, TDestinations[]>>(() => {
    const map = new Map<string, TDestinations[]>()

    for (const row of origDestRows.value ?? []) {
      const key = row.originCode.toUpperCase()
      const existing = map.get(key) ?? []
      existing.push(row)
      map.set(key, existing)
    }

    return map
  })

  const runtimeConfig = useRuntimeConfig()
  const DESTINATION_MAX_SLIDES = 10
  const slidesCache = new Map<string, IImageSlide[]>()

  function getSlides(slideData: TDestinations) {
    const code = slideData.destinCode.toUpperCase()
    const cached = slidesCache.get(code)

    if (cached) return cached

    const slides: IImageSlide[] = new Array(DESTINATION_MAX_SLIDES).fill(0).map((_, i) => {
      const imageNumber = (i + 1).toString().padStart(2, '0')

      return {
        image: `${runtimeConfig.public.btAssets}/images/influence/${code}_${imageNumber}.webp`,
        alt: slideData.destinCity
      }
    })

    slidesCache.set(code, slides)

    return slides
  }

  const selectedOrigin = useState<IOriginItem | null>('selectedOrigin', () => null)

  function shuffleDestinations(destinations: TDestinations[]) {
    for (let i = destinations.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[destinations[i], destinations[j]] = [destinations[j], destinations[i]]
    }
    return destinations
  }

  const currentDestinations = computed<IClpInfluencerDestinations[]>(() => {
    const code = selectedOrigin.value?.code?.toUpperCase()

    if (!code) return []

    const list = shuffleDestinations(mappedData.value.get(code) ?? [])

    return list.map((slide) => ({
      ...slide,
      slides: getSlides(slide)
    }))
  })

  const { isDesktop } = useBTBreakpoints()
</script>
<template>
  <div
    v-if="currentDestinations.length"
    class="bg-grey py-10 lg:pb-14 lg:pt-18"
  >
    <ClpInfluencerBlockDestinationSlider
      :key="selectedOrigin?.code"
      :slides="currentDestinations"
      :title="blok.title"
    >
      <template #top="activeSlide">
        <ClpInfluencerBlockButtons
          v-if="isDesktop"
          :item="activeSlide"
          :cherry-icon="blok.cherryIcon"
          class="mb-8 lg:mb-14"
        />
      </template>
      <template #default="activeSlide">
        <ClpInfluencerBlockImageSlider :slides="activeSlide.slides" />
      </template>
      <template #footer="activeSlide">
        <ClpInfluencerBlockButtons
          v-if="!isDesktop"
          :item="activeSlide"
          :cherry-icon="blok.cherryIcon"
          class="mt-8 lg:mt-14"
        />
      </template>
    </ClpInfluencerBlockDestinationSlider>
  </div>
</template>
